import { useEffect, useRef, useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import type { Page } from '@inertiajs/core'
import { ArrowLeft, Copy, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import ProjectTaskComments from '@/frontend/features/Fde/ProjectTaskComments'
import TaskStructureFields from '@/frontend/features/Fde/TaskStructureFields'
import TaskSubtasks from '@/frontend/features/Fde/TaskSubtasks'
import TaskChecklist from '@/frontend/features/Fde/TaskChecklist'
import ProjectTaskProperties from '@/frontend/features/Fde/ProjectTaskProperties'
import TaskObjective from '@/frontend/features/ProjectObjectives/TaskObjective'
import ProjectTaskLinks from '@/frontend/features/Fde/ProjectTaskLinks'
import ProjectTaskMetadata from '@/frontend/features/Fde/ProjectTaskMetadata'
import type {
  ProjectTaskContext,
  ProjectTaskLinkOptions,
} from '@/frontend/features/Fde/projectTaskTypes'
import {
  changedProjectTaskPayload,
  projectTaskFormValues,
  type ProjectTaskFormData,
  type ScalarTaskField,
  type TaskField,
} from '@/frontend/features/Fde/projectTaskForm'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { FdeCopy, ProjectTask, ProjectTaskAssignee } from '@/frontend/features/Fde/types'

interface Props {
  projectId: string
  task: ProjectTask | null
  assignees: ProjectTaskAssignee[]
  canManage: boolean
  copy: FdeCopy
  headerActions?: React.ReactNode
  context: ProjectTaskContext | null
  linkOptions: ProjectTaskLinkOptions
  defaults?: {
    parent_task_id: string
    task_category_id: string | null
    project_objective_id: string | null
  } | null
}

export default function ProjectTaskDetail({
  projectId,
  task,
  assignees,
  canManage,
  copy,
  headerActions,
  context,
  linkOptions,
  defaults,
}: Props) {
  const initial = () => ({
    ...projectTaskFormValues(task),
    ...(!task && defaults
      ? {
          parent_task_id: defaults.parent_task_id,
          task_category_id: defaults.task_category_id ?? '',
          project_objective_id: defaults.project_objective_id ?? '',
        }
      : {}),
  })
  const form = useForm<ProjectTaskFormData>(initial())
  const editedFieldsRef = useRef<Set<TaskField>>(new Set())
  const activeTaskIdRef = useRef(task?.id)
  const [editedFieldCount, setEditedFieldCount] = useState(0)
  const [editing, setEditing] = useState(!task)
  const editingVersionRef = useRef(task?.lock_version)
  const [quickSaving, setQuickSaving] = useState(false)
  const [linkNotice, setLinkNotice] = useState('')
  const savedAssigneeValues = task?.assignees.map((assignee) => assignee.value) ?? []
  const participantOptions = assignees.map(
    (option) => task?.assignees.find((assignment) => assignment.value === option.value) ?? option,
  )
  useEffect(() => {
    if (!linkNotice) return
    const timer = window.setTimeout(() => setLinkNotice(''), 2500)
    return () => window.clearTimeout(timer)
  }, [linkNotice])

  function quickChange(
    field: 'status' | 'priority' | 'related_task_ids' | 'repo_paths',
    value: string | string[],
  ) {
    if (!task || !canManage || editing || quickSaving || form.processing) return
    if ((field === 'status' || field === 'priority') && value === task[field]) return
    router.patch(
      fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId),
      {
        project_task: { [field]: value, lock_version: task.lock_version },
      },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setQuickSaving(true),
        onFinish: () => setQuickSaving(false),
      },
    )
  }
  async function copyLink() {
    if (!task) return
    try {
      await navigator.clipboard.writeText(
        new URL(
          fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId),
          window.location.origin,
        ).href,
      )
      setLinkNotice(copy.management.link_copied)
    } catch {
      setLinkNotice(copy.management.copy_link_failed)
    }
  }

  useEffect(() => {
    const next = initial()
    if (activeTaskIdRef.current !== task?.id) {
      editingVersionRef.current = task?.lock_version
      activeTaskIdRef.current = task?.id
      setEditing(!task)
      editedFieldsRef.current.clear()
      setEditedFieldCount(0)
      form.setData(next)
    } else {
      const edited = editedFieldsRef.current
      form.setData({
        title: edited.has('title') ? form.data.title : next.title,
        project_objective_id: edited.has('project_objective_id')
          ? form.data.project_objective_id
          : next.project_objective_id,
        task_category_id: edited.has('task_category_id')
          ? form.data.task_category_id
          : next.task_category_id,
        parent_task_id: edited.has('parent_task_id')
          ? form.data.parent_task_id
          : next.parent_task_id,
        description: edited.has('description') ? form.data.description : next.description,
        status: edited.has('status') ? form.data.status : next.status,
        priority: edited.has('priority') ? form.data.priority : next.priority,
        start_on: edited.has('start_on') ? form.data.start_on : next.start_on,
        due_on: edited.has('due_on') ? form.data.due_on : next.due_on,
        assignee_refs: edited.has('assignee_refs') ? form.data.assignee_refs : next.assignee_refs,
        notify_agent_refs: form.data.notify_agent_refs,
      })
    }
    form.clearErrors()
  }, [task?.id, task?.updated_at])

  function submit() {
    const payload = task ? changedProjectTaskPayload(form.data, editedFieldsRef.current) : form.data
    form.transform(() => ({
      project_task: { ...payload, ...(task ? { lock_version: editingVersionRef.current } : {}) },
    }))
    const path = task ? `${fdeRoutes.projectTasks}/${task.id}` : fdeRoutes.projectTasks
    const options = {
      preserveScroll: true,
      // Validation redirects carry an alert; keep the draft open for correction.
      onSuccess: (page: Page) => {
        if (!(page.props.flash as { alert?: string } | undefined)?.alert) clearEditedFields()
      },
    }
    if (task) form.patch(fdeProjectPath(path, projectId), options)
    else form.post(fdeProjectPath(path, projectId), options)
  }

  function changeField(field: ScalarTaskField, value: string) {
    editedFieldsRef.current.add(field)
    setEditedFieldCount(editedFieldsRef.current.size)
    form.setData(field, value)
  }

  function changeAssignees(values: string[]) {
    const addedAgentRefs = values.filter(
      (value) =>
        value.startsWith('agent:') &&
        !form.data.assignee_refs.includes(value) &&
        !savedAssigneeValues.includes(value),
    )
    const notificationValues = [
      ...form.data.notify_agent_refs.filter((value) => values.includes(value)),
      ...addedAgentRefs,
    ].filter((value, index, entries) => entries.indexOf(value) === index)
    editedFieldsRef.current.add('assignee_refs')
    setEditedFieldCount(editedFieldsRef.current.size)
    form.setData({ ...form.data, assignee_refs: values, notify_agent_refs: notificationValues })
  }

  function clearEditedFields() {
    editedFieldsRef.current.clear()
    setEditedFieldCount(0)
    setEditing(false)
  }

  function cancel() {
    form.setData(projectTaskFormValues(task))
    clearEditedFields()
  }

  function beginEditing() {
    editingVersionRef.current = task?.lock_version
    setEditing(true)
  }

  function remove() {
    if (!task || !window.confirm(copy.management.confirm_delete_task)) return
    router.delete(fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId))
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex min-h-14 shrink-0 items-center gap-3 border-b border-border px-4">
        <Button asChild size="icon-sm" variant="ghost">
          <Link
            href={fdeProjectPath(fdeRoutes.tasks, projectId)}
            aria-label={copy.management.back_to_tasks}
          >
            <ArrowLeft />
          </Link>
        </Button>
        <div className="flex min-w-0 flex-1 items-center">
          <Link
            href={fdeProjectPath(fdeRoutes.tasks, projectId)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {copy.management.tasks}
          </Link>
          <span className="mx-2 text-muted-foreground/40">/</span>
          <span className="truncate text-xs font-medium" title={task?.title}>
            {task ? task.identifier : copy.management.add_task}
          </span>
        </div>
        {headerActions}
        {(quickSaving || linkNotice) && (
          <p role="status" className="hidden text-xs text-muted-foreground sm:block">
            {quickSaving ? copy.management.saving : linkNotice}
          </p>
        )}
        {canManage && task && !editing && (
          <Button size="sm" variant="outline" disabled={quickSaving} onClick={beginEditing}>
            <Pencil className="size-3.5" />
            {copy.management.edit_task}
          </Button>
        )}
        {canManage && editing && (
          <>
            {task && (
              <Button size="sm" variant="ghost" disabled={form.processing} onClick={cancel}>
                {copy.common.cancel}
              </Button>
            )}
            <Button
              size="sm"
              disabled={
                form.processing ||
                !form.data.title.trim() ||
                Boolean(task && editedFieldCount === 0)
              }
              onClick={submit}
            >
              {copy.common.save}
            </Button>
          </>
        )}
        {task && canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon-sm" variant="ghost" aria-label={copy.management.task_actions}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={copyLink}>
                <Copy />
                {copy.management.copy_task_link}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={quickSaving || form.processing}
                onSelect={remove}
              >
                <Trash2 />
                {copy.management.delete_task}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>
      <div
        data-testid="project-task-scroll"
        className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_18rem] lg:overflow-hidden"
      >
        <main className="min-w-0 px-5 py-8 lg:overflow-y-auto lg:px-10 xl:px-16">
          <div className="mx-auto max-w-3xl">
            {Object.values(form.errors).some(Boolean) && (
              <p className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {Object.values(form.errors).filter(Boolean).join(' ')}
              </p>
            )}
            {editing && canManage ? (
              <>
                <Input
                  aria-label={copy.management.task_title}
                  value={form.data.title}
                  onChange={(event) => changeField('title', event.target.value)}
                  placeholder={copy.management.task_title}
                  className="h-auto border-0 px-0 text-2xl font-semibold shadow-none focus-visible:ring-0"
                />
                <Textarea
                  aria-label={copy.management.task_description}
                  value={form.data.description}
                  onChange={(event) => changeField('description', event.target.value)}
                  placeholder={copy.management.task_description_placeholder}
                  rows={8}
                  className="mb-7 mt-4 resize-y text-sm leading-6"
                />
              </>
            ) : (
              <>
                <h2 className="text-2xl font-semibold leading-8 tracking-tight">{task?.title}</h2>
                {task && <ProjectTaskMetadata task={task} copy={copy} />}
                {task?.description && (
                  <SimpleMarkdown
                    content={task.description}
                    allowImages={false}
                    className="mb-8 mt-5 break-words text-sm leading-7 text-foreground/85 [&_code]:break-all [&_h2]:mt-7 [&_h3]:mt-6 [&_li]:leading-7 [&_ul]:list-outside [&_ul]:pl-5 [&_ol]:list-outside [&_ol]:pl-5"
                  />
                )}
              </>
            )}
            {task && !editing && (
              <>
                <TaskSubtasks task={task} projectId={projectId} copy={copy} canManage={canManage} />
                <TaskChecklist
                  key={task.id}
                  task={task}
                  projectId={projectId}
                  copy={copy}
                  canManage={canManage}
                />
              </>
            )}
            {task && context && (
              <ProjectTaskLinks
                projectId={projectId}
                context={context}
                options={linkOptions}
                canManage={canManage && !editing}
                disabled={quickSaving || form.processing}
                copy={copy}
                onChange={quickChange}
              />
            )}
            {task && (
              <ProjectTaskComments
                projectId={projectId}
                taskId={task.id}
                comments={task.comments}
                agents={task.assignees.filter((assignee) => assignee.type === 'agent')}
                canManage={canManage}
                copy={copy}
              />
            )}
          </div>
        </main>
        <ProjectTaskProperties
          data={form.data}
          task={task}
          options={participantOptions}
          editing={editing && canManage}
          canManage={canManage}
          disabled={quickSaving || form.processing}
          copy={copy}
          onField={changeField}
          onQuickField={quickChange}
          onParticipants={changeAssignees}
          onNotify={(values) => form.setData('notify_agent_refs', values)}
        >
          <TaskObjective
            task={task}
            options={linkOptions.objectives || []}
            projectId={projectId}
            editing={editing && canManage}
            value={form.data.project_objective_id}
            disabled={quickSaving || form.processing}
            onChange={(value) => changeField('project_objective_id', value)}
            copy={copy.management}
          />
          <TaskStructureFields
            projectId={projectId}
            task={task}
            options={linkOptions}
            copy={copy}
            editing={editing && canManage}
            canManage={canManage}
            disabled={quickSaving || form.processing}
            categoryId={form.data.task_category_id}
            parentId={form.data.parent_task_id}
            onChange={changeField}
          />
        </ProjectTaskProperties>
      </div>
    </div>
  )
}
